import { useState, useEffect, useRef, useCallback } from "react";
import {
  MessageCircle, CheckCheck, Check, Clock, AlertCircle, Phone,
  Send, Paperclip, X, Image, FileText, Volume2, Video, MapPin,
  User as UserIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Media type display config ──────────────────────────────────────────────────
const MEDIA_ICONS = {
  DOCUMENT: FileText,
  AUDIO:    Volume2,
  VIDEO:    Video,
  LOCATION: MapPin,
  CONTACT:  UserIcon,
};

function DeliveryIcon({ status }) {
  if (status === "READ")      return <CheckCheck className="w-3 h-3 text-blue-500" />;
  if (status === "DELIVERED") return <CheckCheck className="w-3 h-3 text-[#706E6B]" />;
  if (status === "SENT")      return <Check className="w-3 h-3 text-[#706E6B]" />;
  if (status === "FAILED")    return <AlertCircle className="w-3 h-3 text-red-500" />;
  return <Clock className="w-3 h-3 text-[#C9C9C9]" />;
}

function formatTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit", hour12: true,
    });
  } catch { return iso; }
}

// ── Media content renderer (B5) ────────────────────────────────────────────────
function MediaContent({ msg, isOutbound }) {
  const { message_type, original_content, media_url } = msg;

  if (message_type === "IMAGE") {
    return (
      <div className="space-y-1">
        {media_url ? (
          <img
            src={media_url}
            alt="WhatsApp image"
            className="rounded-lg max-w-full max-h-48 object-cover border border-black/10"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="flex items-center gap-1.5 bg-black/10 rounded p-2">
            <Image className="w-4 h-4" />
            <span className="text-xs">[Image]</span>
          </div>
        )}
        {original_content && <p className="text-sm">{original_content}</p>}
      </div>
    );
  }

  if (MEDIA_ICONS[message_type]) {
    const Icon = MEDIA_ICONS[message_type];
    return (
      <div className="space-y-1">
        <div className={`flex items-center gap-2 rounded-lg p-2.5 ${isOutbound ? "bg-white/20" : "bg-black/8"}`}>
          <Icon className="w-4 h-4 flex-shrink-0" />
          <div className="min-w-0">
            <span className="text-xs font-medium block">
              {message_type.charAt(0) + message_type.slice(1).toLowerCase()}
            </span>
            {original_content && (
              <span className="text-xs opacity-70 block truncate max-w-48">{original_content}</span>
            )}
          </div>
          {media_url && (
            <a
              href={media_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] underline opacity-70 hover:opacity-100 ml-auto"
            >
              Open
            </a>
          )}
        </div>
      </div>
    );
  }

  // TEXT or TEMPLATE or INTERACTIVE
  return <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{original_content}</p>;
}

// ── Single message bubble ──────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const isOutbound = msg.direction === "OUTBOUND";

  return (
    <div
      className={`flex gap-2 mb-3 ${isOutbound ? "justify-end" : "justify-start"}`}
      data-testid={`message-bubble-${msg.message_id}`}
    >
      {!isOutbound && (
        <div className="w-7 h-7 rounded-full bg-[#EEF4FF] border border-[#C9DBF2] flex items-center justify-center flex-shrink-0 mt-0.5">
          <Phone className="w-3 h-3 text-[#0176D3]" />
        </div>
      )}

      <div className={`max-w-[70%] flex flex-col ${isOutbound ? "items-end" : "items-start"}`}>
        <span className="text-[10px] text-[#706E6B] mb-0.5">
          {isOutbound
            ? (msg.from_identity?.name || "Prashasta Events")
            : (msg.from_identity?.name || msg.from_identity?.phone || "Customer")}
        </span>

        <div
          className={`px-3 py-2 rounded-xl shadow-sm ${
            isOutbound
              ? "bg-[#0176D3] text-white rounded-br-sm"
              : "bg-[#F3F3F3] text-[#181818] border border-[#E5E5E5] rounded-bl-sm"
          }`}
        >
          <MediaContent msg={msg} isOutbound={isOutbound} />
        </div>

        <div className={`flex items-center gap-1 mt-0.5 ${isOutbound ? "flex-row-reverse" : "flex-row"}`}>
          <span className="text-[10px] text-[#C9C9C9]">{formatTime(msg.occurred_at)}</span>
          {isOutbound && <DeliveryIcon status={msg.delivery_status} />}
        </div>
      </div>

      {isOutbound && (
        <div className="w-7 h-7 rounded-full bg-[#EEF4FF] border border-[#C9DBF2] flex items-center justify-center flex-shrink-0 mt-0.5">
          <MessageCircle className="w-3 h-3 text-[#0176D3]" />
        </div>
      )}
    </div>
  );
}

// ── B5: Attach media bar ───────────────────────────────────────────────────────
function AttachBar({ mediaUrl, setMediaUrl, onClose }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-[#EEF4FF] border-t border-[#C9DBF2]">
      <Image className="w-3.5 h-3.5 text-[#0176D3] flex-shrink-0" />
      <input
        type="url"
        value={mediaUrl}
        onChange={(e) => setMediaUrl(e.target.value)}
        placeholder="Paste image URL (https://...)"
        className="flex-1 text-xs bg-transparent border-none outline-none text-[#181818] placeholder-[#C9C9C9]"
        data-testid="media-url-input"
      />
      {mediaUrl && (
        <button onClick={() => setMediaUrl("")} className="text-[#706E6B] hover:text-[#181818]">
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── B4: Compose area ───────────────────────────────────────────────────────────
function ComposeArea({ journeyId, context, onSent }) {
  const [content, setContent] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [showAttach, setShowAttach] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const handleSend = async () => {
    const text = content.trim();
    if (!text) return;
    setSending(true);
    setError(null);
    const token = localStorage.getItem("token");
    const body = { content: text };
    if (mediaUrl.trim()) {
      body.media_url = mediaUrl.trim();
      body.message_type = "IMAGE";
    }
    try {
      const r = await fetch(`${API}/api/journeys/${journeyId}/messages/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to send");
      }
      const msg = await r.json();
      setContent("");
      setMediaUrl("");
      setShowAttach(false);
      onSent(msg);
      inputRef.current?.focus();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-[#E5E5E5] bg-white" data-testid="compose-area">
      {/* Customer label */}
      {context?.customer_phone && (
        <div className="flex items-center gap-1.5 px-3 pt-2 pb-0">
          <Phone className="w-3 h-3 text-[#C9C9C9]" />
          <span className="text-[10px] text-[#706E6B]">
            Replying to{" "}
            <strong className="text-[#444444]">{context.customer_name || "Customer"}</strong>
            {" "}·{" "}
            <span className="font-mono">{context.customer_phone}</span>
          </span>
        </div>
      )}

      {/* Attach bar (B5) */}
      {showAttach && (
        <AttachBar
          mediaUrl={mediaUrl}
          setMediaUrl={setMediaUrl}
          onClose={() => { setShowAttach(false); setMediaUrl(""); }}
        />
      )}

      {/* Input row */}
      <div className="flex items-end gap-2 p-2">
        <button
          data-testid="attach-media-btn"
          onClick={() => setShowAttach((s) => !s)}
          title="Attach image by URL (B5)"
          className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
            showAttach || mediaUrl
              ? "bg-[#EEF4FF] text-[#0176D3]"
              : "text-[#706E6B] hover:bg-[#F3F3F3] hover:text-[#181818]"
          }`}
        >
          <Paperclip className="w-4 h-4" />
        </button>

        <div className="flex-1 relative">
          <textarea
            ref={inputRef}
            data-testid="compose-input"
            value={content}
            onChange={(e) => { setContent(e.target.value); setError(null); }}
            onKeyDown={handleKey}
            placeholder="Type a message…"
            rows={1}
            className="w-full resize-none border border-[#E5E5E5] rounded-xl px-3 py-2 text-sm text-[#181818] placeholder-[#C9C9C9] focus:outline-none focus:border-[#0176D3] transition-colors leading-relaxed"
            style={{ maxHeight: "120px", overflowY: "auto" }}
          />
          {mediaUrl && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <Image className="w-3.5 h-3.5 text-[#0176D3]" title="Image attached" />
            </div>
          )}
        </div>

        <button
          data-testid="send-message-btn"
          onClick={handleSend}
          disabled={sending || !content.trim()}
          className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-[#0176D3] hover:bg-[#005FB2] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-xl transition-colors"
        >
          {sending ? <Clock className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-1.5 px-3 pb-2 text-xs text-red-600">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
}

// ── Main JourneyMessages component ─────────────────────────────────────────────
export function JourneyMessages({ journeyId }) {
  const [data, setData] = useState(null);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);
  const token = localStorage.getItem("token");

  const fetchMessages = useCallback(() => {
    if (!journeyId) return;
    fetch(`${API}/api/journeys/${journeyId}/messages`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setData(d); setError(null); })
      .catch(() => setError("Failed to load messages"))
      .finally(() => setLoading(false));
  }, [journeyId, token]);

  const fetchContext = useCallback(() => {
    if (!journeyId) return;
    fetch(`${API}/api/journeys/${journeyId}/messages/context`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setContext(d); })
      .catch(() => {});
  }, [journeyId, token]);

  useEffect(() => {
    setLoading(true);
    fetchMessages();
    fetchContext();
  }, [fetchMessages, fetchContext]);

  // Auto-refresh every 15s to pick up delivery status changes (Read Receipts)
  useEffect(() => {
    if (!journeyId) return;
    const interval = setInterval(() => {
      fetchMessages();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchMessages]);

  useEffect(() => {
    if (data?.messages?.length) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [data]);

  const handleSent = (newMsg) => {
    setData((prev) => ({
      ...prev,
      total: (prev?.total || 0) + 1,
      messages: [...(prev?.messages || []), newMsg],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#706E6B] text-sm" data-testid="journey-messages-loading">
        <Clock className="w-4 h-4 mr-2 animate-spin" />
        Loading conversation…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-red-500 text-sm gap-2" data-testid="journey-messages-error">
        <AlertCircle className="w-5 h-5" />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="journey-messages-panel">
      {/* Channel header */}
      <div className="flex items-center justify-between px-4 py-2 bg-[#F3F3F3] border-b border-[#E5E5E5] flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs font-medium text-[#444444]">WhatsApp</span>
          <span className="text-xs text-[#706E6B]">· Mock Provider</span>
        </div>
        <span className="text-xs text-[#706E6B]" data-testid="journey-messages-count">
          {data?.total || 0} message{(data?.total || 0) !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Thread */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-white" style={{ minHeight: 0 }}>
        {!data || data.total === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-[#706E6B] gap-3" data-testid="journey-messages-empty">
            <MessageCircle className="w-10 h-10 text-[#C9C9C9]" />
            <p className="text-sm font-medium">No messages yet</p>
            <p className="text-xs text-[#C9C9C9]">Inbound WhatsApp messages appear here</p>
          </div>
        ) : (
          data.messages.map((msg) => <MessageBubble key={msg.message_id} msg={msg} />)
        )}
        <div ref={bottomRef} />
      </div>

      {/* Compose area (B4) */}
      {context?.can_send !== false && (
        <ComposeArea
          journeyId={journeyId}
          context={context}
          onSent={handleSent}
        />
      )}
    </div>
  );
}
